import {
  TMeeting,
  TCreateMeetingPayload,
  TMeetingsResponse,
  TMeetingsFilters,
  TMeetingStatusCatalogItem,
} from "@/models/meetings.model";
import axios from "@utils/axios-instance";
import { isAxiosError } from "axios";

const create = async (data: TCreateMeetingPayload): Promise<TMeeting> => {
  const response = await axios.post("/meeting", data);
  return response.data;
};

const getAll = async (filters: TMeetingsFilters = {}): Promise<TMeetingsResponse> => {
  const cleanFilters = Object.fromEntries(
    Object.entries(filters).filter(([, val]) => val !== undefined && val !== null && val !== "")
  );
  const queryString = new URLSearchParams(cleanFilters as Record<string, string>).toString();
  const response = await axios.get(`/meeting${queryString ? `?${queryString}` : ""}`);
  return response.data;
};

const updateById = async (
  id: number,
  data: Partial<Omit<TMeeting, "id" | "uuid" | "companyId" | "created" | "updated">>
): Promise<TMeeting> => {
  const response = await axios.patch(`/meeting/${id}`, data);
  return response.data;
};

const setStatusById = async (
  id: number,
  status: string,
  reason?: string
): Promise<TMeeting> => {
  const response = await axios.patch(`/meeting/${id}/status`, {
    status,
    reason,
  });
  return response.data;
};

const deleteById = async (id: number): Promise<void> => {
  await axios.delete(`/meeting/${id}`);
};

/**
 * Meeting linked to a video call (Zoom sessionId, which carries "==" so it
 * must be URL-encoded). 404 means the call has no meeting yet — not an error.
 */
const getBySessionId = async (sessionId: string): Promise<TMeeting | null> => {
  try {
    const response = await axios.get(`/call/${encodeURIComponent(sessionId)}/meeting`);
    return response.data;
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 404) return null;
    throw error;
  }
};

/** Every meeting of a lead. Backend only paginates (no status filter), so callers fetch a large page and filter client-side. */
const getByLeadId = async (
  leadId: number,
  params: { page?: number; limit?: number } = {}
): Promise<TMeetingsResponse> => {
  const response = await axios.get(`/lead/${leadId}/meetings`, {
    params: { page: params.page ?? 1, limit: params.limit ?? 100 },
  });
  return response.data;
};

/**
 * Permanently links a video call to a meeting and returns the updated meeting.
 * Omitting both status fields lets the backend decide (an ended call with a
 * meetingId marks it completed); `statusId` points at the status catalog.
 */
const linkToSession = async (
  sessionId: string,
  payload: { meetingId: number; status?: string; statusId?: number }
): Promise<TMeeting> => {
  const response = await axios.put(`/call/${encodeURIComponent(sessionId)}/meeting`, payload);
  return response.data;
};

/** Meeting-status catalog: the system statuses plus the company's own. */
const getStatusCatalog = async (): Promise<TMeetingStatusCatalogItem[]> => {
  const response = await axios.get("/meeting/status");
  return response.data;
};

export default {
  create,
  getAll,
  updateById,
  setStatusById,
  deleteById,
  getBySessionId,
  getByLeadId,
  linkToSession,
  getStatusCatalog,
};
